import { osName, osVersion, isAndroid, isIOS, isMacOs, isWindows } from 'react-device-detect';

/**
 * Utility class for detecting client device properties
 */
export default class ClientDeviceUtils {
    
    private static wasLastDownEventFromTouchscreen: boolean | undefined = undefined;
    
    /**
     * Checks if the device supports touch input
     * @returns boolean indicating if touch is supported
     */
    static isTouchSupported(): boolean {
        const maxTouchPoints = navigator.maxTouchPoints;
        console.debug('Touch points count:', maxTouchPoints);
        
        return maxTouchPoints > 0;
    }

    /**
     * Gets whether the last pointer down event was from a touchscreen
     * @returns boolean | undefined - true if touch, false if mouse, undefined if unknown
     */
    static getWasLastDownEventFromTouchscreen(): boolean | undefined {
        return ClientDeviceUtils.wasLastDownEventFromTouchscreen;
    }

    /**
     * Determines if touch control is being used by combining device capability and last event
     * @returns boolean - true if touch control is used, false otherwise
     */
    static isTouchControlUsed(): boolean {
        try {
            const touchSupported = ClientDeviceUtils.isTouchSupported();
            const lastEventFromTouch = ClientDeviceUtils.getWasLastDownEventFromTouchscreen();
            
            // If no event has been recorded yet, return what is available from 'isTouchSupported'
            if (lastEventFromTouch === undefined) {
                return touchSupported;
            }
            
            // Combine using AND logic: both device supports touch AND last event was from touch
            return touchSupported && lastEventFromTouch;
        } catch (error) {
            console.warn('Error in isTouchControlUsed:', error);
            return true; // Return true on error (assume touch)
        }
    }

    /**
     * Gets the operating system information from react-device-detect
     * @returns string - operating system name and version
     */
    static getOperatingSystem(): string {
        const os = `${osName} ${osVersion}`.trim();
        console.info('Operating System detected:', os);
        return os;
    }

    /**
     * Handles navigation to a location based on device type
     * @param lat Latitude coordinate
     * @param lng Longitude coordinate
     * @param placeName Name of the place for navigation (not used in current implementation)
     * @param windowObj Window object (passed as parameter for testing)
     */
    static handleNavigation(lat: number, lng: number, placeName: string, windowObj: Window = window): void {
        // Android: Use geo protocol for navigation apps
        if (isAndroid) {
            // const geoUrl = `geo:${lat},${lng}`;
            const geoUrl = `geo:0,0?q=${encodeURIComponent(placeName)}@${lat},${lng}`;
            windowObj.location.href = geoUrl;
            return;
        }
        
        // iOS (iPhone/iPad): Use Apple Maps protocol
        if (isIOS) {
            ClientDeviceUtils.openAppleMaps(lat, lng, windowObj);
            return;
        }
        
        // macOS: Use Apple Maps or Google Maps
        if (isMacOs) {
            // Try Apple Maps first, fallback to Google Maps
            ClientDeviceUtils.openAppleMaps(lat, lng, windowObj);
            return;
        }
        
        // Windows: Use Google Maps
        if (isWindows) {
            ClientDeviceUtils.openGoogleMaps(lat, lng, windowObj);
            return;
        }
        
        // Fallback: Use Google Maps for unknown platforms
        ClientDeviceUtils.openGoogleMaps(lat, lng, windowObj);
    }

    /**
     * Opens Apple Maps with the specified coordinates
     * @param lat Latitude coordinate
     * @param lng Longitude coordinate
     * @param windowObj Window object (passed as parameter for testing)
     */
    private static openAppleMaps(lat: number, lng: number, windowObj: Window = window): void {
        const appleMapsUrl = `http://maps.apple.com/?q=${lat},${lng}`;
        windowObj.location.href = appleMapsUrl;
    }

    /**
     * Opens Google Maps with the specified coordinates
     * @param lat Latitude coordinate
     * @param lng Longitude coordinate
     * @param windowObj Window object (passed as parameter for testing)
     */
    private static openGoogleMaps(lat: number, lng: number, windowObj: Window = window): void {
        const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
        windowObj.open(googleMapsUrl, '_blank');
    }


    /**
     * Handles pointer down events and logs the pointer type
     * @param event The UIEvent (MouseEvent from Leaflet)
     */
    static handlePointerDown(event: UIEvent): void {
        console.debug('Pointer down event:', event);
        // sourceCapabilities is experimental API - use type assertion
        const eventWithCapabilities = event as UIEvent & { sourceCapabilities?: { firesTouchEvents?: boolean } };
        const firesTouchEvents = eventWithCapabilities.sourceCapabilities?.firesTouchEvents;
        console.debug('Fires touch events:', firesTouchEvents);
        
        // Set static variable based on firesTouchEvents
        if (firesTouchEvents !== undefined) {
            ClientDeviceUtils.wasLastDownEventFromTouchscreen = firesTouchEvents;
        } else {
            ClientDeviceUtils.wasLastDownEventFromTouchscreen = undefined;
        }
    }

    /**
     * Checks if the device has a camera available
     * @returns Promise<boolean> indicating if camera is available
     */
    static async hasCameraSupport(): Promise<boolean> {
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                console.debug('Media devices API not supported');
                return false;
            }

            const devices = await navigator.mediaDevices.enumerateDevices();
            const hasCamera = devices.some(device => device.kind === 'videoinput');
            console.debug('Camera support detected:', hasCamera);
            return hasCamera;
        } catch (error) {
            console.warn('Error checking camera support:', error);
            return false;
        }
    }

    /**
     * Checks if the current device is a mobile device (phone or tablet)
     * This is used to determine if camera capture should be available
     * Based on OS and touch support - excludes desktop/laptop devices
     * @returns boolean indicating if device is mobile (phone/tablet)
     */
    static isMobileDevice(): boolean {
        // Only Android and iOS devices (phones, tablets, iPads)
        // This excludes Windows laptops, MacBooks, and desktop computers
        return (isAndroid || isIOS) && ClientDeviceUtils.isTouchSupported();
    }

    /**
     * Opens camera to capture a photo
     * @returns Promise<File | null> - captured photo as File object or null if cancelled/failed
     */
    static async capturePhoto(): Promise<File | null> {
        try {
            // Request camera access
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }, // Use rear camera by default
                audio: false
            });

            // Create video element to display camera stream
            const video = document.createElement('video');
            video.srcObject = stream;
            video.setAttribute('playsinline', 'true'); // Important for iOS
            await video.play();

            // Wait for video metadata to load with timeout
            await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('Video metadata loading timeout'));
                }, 10000); // 10 second timeout
                
                video.onloadedmetadata = () => {
                    clearTimeout(timeout);
                    resolve();
                };
                
                video.onerror = () => {
                    clearTimeout(timeout);
                    reject(new Error('Video loading error'));
                };
            });

            // Create canvas to capture frame
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const context = canvas.getContext('2d');
            
            if (!context) {
                throw new Error('Failed to get canvas context');
            }

            // Draw current video frame to canvas
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Stop camera stream
            stream.getTracks().forEach(track => track.stop());

            // Convert canvas to blob and then to File
            return new Promise<File | null>((resolve) => {
                canvas.toBlob((blob) => {
                    if (blob) {
                        const timestamp = new Date().getTime();
                        const file = new File([blob], `camera-photo-${timestamp}.jpg`, {
                            type: 'image/jpeg',
                            lastModified: timestamp
                        });
                        resolve(file);
                    } else {
                        resolve(null);
                    }
                }, 'image/jpeg', 0.95);
            });
        } catch (error) {
            console.error('Error capturing photo:', error);
            return null;
        }
    }
} 